import { type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganisationSlugFields } from '../../lib/organisation/useOrganisationSlugFields';

type AdminCreateOrganisationCardProps = {
  creating?: boolean;
  error?: string | null;
  onCreate: (input: { name: string; slug: string }) => void;
};

export function AdminCreateOrganisationCard({
  creating = false,
  error = null,
  onCreate,
}: AdminCreateOrganisationCardProps) {
  const { t } = useTranslation('admin');
  const { name, slug, onNameChange, onSlugChange } = useOrganisationSlugFields();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onCreate({ name: name.trim(), slug: slug.trim() });
  };

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      <h2 className="m-0 text-base font-bold text-ink">{t('createOrg.title')}</h2>
      <p className="m-0 mt-1 text-sm text-muted">{t('createOrg.description')}</p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-3">
        <label className="block text-xs font-semibold text-muted">
          {t('createOrg.nameLabel')}
          <input
            required
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="mt-1 w-full rounded-[10px] border border-line bg-field px-3 py-2.5 text-sm text-ink"
            placeholder={t('createOrg.namePlaceholder')}
          />
        </label>
        <label className="block text-xs font-semibold text-muted">
          {t('createOrg.slugLabel')}
          <input
            required
            value={slug}
            onChange={(e) => onSlugChange(e.target.value)}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            className="mt-1 w-full rounded-[10px] border border-line bg-field px-3 py-2.5 text-sm text-ink"
            placeholder={t('createOrg.slugPlaceholder')}
          />
        </label>
        {error ? (
          <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={creating || !name.trim() || !slug.trim()}
          className="w-full rounded-[10px] px-4 py-[11px] text-sm font-bold text-[var(--gs-accent-ink)] disabled:opacity-60"
          style={{ background: 'var(--gs-accent-solid)' }}
        >
          {creating ? t('createOrg.creating') : t('createOrg.submit')}
        </button>
      </form>
    </section>
  );
}
